import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';

/*
    Location Search Bar, used in the StoreSearchForm.es6 file
*/
class LocationSearchBar extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            value: '',
            suggestions: [] // Start with empty suggestions for better UX
        };
    }

    onSuggestionsClearRequested = () => {
        this.setState({
            suggestions: []
        });
    };

    onSuggestionsFetchRequested = ({ value }) => {
        const inputValue = value.trim().toLowerCase();
        const inputLength = inputValue.length;

        // Safety check for storesJSONData
        if (!this.props.storesJSONData || !Array.isArray(this.props.storesJSONData)) {
            console.warn('LocationSearchBar: storesJSONData is not a valid array', this.props.storesJSONData);
            this.setState({ suggestions: [] });
            return;
        }

        const filteredSuggestions = inputLength === 0 ? [] : this.props.storesJSONData.filter(store => {
            // Safety check for each store object
            if (!store || (typeof store !== 'string' && typeof store.label !== 'string')) {
                return false;
            }

            const searchText = typeof store === 'string' ? store : store.label;
            return searchText.toLowerCase().slice(0, inputLength) === inputValue;
        });

        this.setState({
            suggestions: filteredSuggestions
        });
    };

    onChange = (event, { newValue }) => {
        this.setState({
            value: newValue
        });

        if (this.props.inputFieldChangeHandler) {
            this.props.inputFieldChangeHandler(newValue);
        }
    };

    onSuggestionSelected = (event, { suggestion }) => {
        // Get the display value for the selected suggestion
        let selectedValue = '';
        if (typeof suggestion === 'string') {
            selectedValue = suggestion;
        } else if (suggestion && suggestion.label) {
            selectedValue = String(suggestion.label);
        } else {
            selectedValue = 'Invalid suggestion';
        }

        // Update the input value with the selected suggestion
        this.setState({
            value: selectedValue,
            suggestions: [] // Clear suggestions after selection
        });

        // Notify parent component about the selection
        if (this.props.onSuggestionSelected) {
            this.props.onSuggestionSelected(suggestion);
        }

        // Optionally trigger input change handler
        if (this.props.inputFieldChangeHandler) {
            this.props.inputFieldChangeHandler(selectedValue);
        }

        // Optionally auto-submit when suggestion is selected
        if (this.props.autoSubmitOnSelection && this.props.onSubmitHandler) {
            // Small delay to ensure state update completes
            setTimeout(() => {
                this.props.onSubmitHandler();
            }, 10);
        }
    };

    render() {
        const { value, suggestions } = this.state;

        const inputProps = {
            type: 'text',
            name: value ? this.props.inputFieldName : '',
            placeholder: this.props.inputFieldPlaceholder,
            value: value,
            id: 'LocationSearchBar__input',
            onChange: this.onChange
        };
        const theme = {
            container: 'LocationSearchBar__Container',
            suggestionsContainer: 'LocationSearchBar__AutocompleteMenu',
            suggestionsList: '',
            suggestion: 'LocationSearchBar__AutocompleteItem',
            suggestionHighlighted: 'LocationSearchBar__AutocompleteItem LocationSearchBar__AutocompleteItem--highlighted'
        };

        const renderSuggestionsContainer = ({ containerProps, children }) => {
            return (
                <div {...containerProps}>
                    {children}
                </div>
            );
        };

        return (
            <div className="LocationSearchBar">
                <button
                    className="button LocationSearchBar__locationSearch"
                    onClick={this.props.onClickCurrentHandler}
                    title={this.props.findByCurrentLocationTitle}
                    aria-label={this.props.findByCurrentLocationTitle}
                    type="button"
                />

                <label htmlFor="LocationSearchBar__input" className="show-for-sr">
                    {this.props.inputLabelText}
                </label>

                <Autosuggest
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    onSuggestionSelected={this.onSuggestionSelected}
                    getSuggestionValue={suggestion => {
                        if (typeof suggestion === 'string') {
                            return suggestion;
                        } else if (suggestion && suggestion.label) {
                            return String(suggestion.label);
                        }
                        return 'Invalid suggestion';
                    }}
                    renderSuggestion={suggestion => {
                        let displayText = '';
                        if (typeof suggestion === 'string') {
                            displayText = suggestion;
                        } else if (suggestion && suggestion.label) {
                            displayText = String(suggestion.label);
                        } else {
                            displayText = 'Invalid suggestion';
                        }
                        return displayText;
                    }}
                    renderSuggestionsContainer={renderSuggestionsContainer}
                    inputProps={inputProps}
                    theme={theme}
                />

                <button
                    className="button LocationSearchBar__magnifyingSearch"
                    type="submit"
                    onClick={this.props.onSubmitHandler}
                    aria-label={this.props.findByAddressTitle}
                />
            </div>
        );
    }
}

LocationSearchBar.propTypes = {
    findByCurrentLocationTitle: PropTypes.string,
    findByAddressTitle: PropTypes.string,
    onClickCurrentHandler: PropTypes.func,
    onSuggestionSelected: PropTypes.func,
    onSubmitHandler: PropTypes.func,
    autoSubmitOnSelection: PropTypes.bool,
    inputFieldName: PropTypes.string,
    inputFieldPlaceholder: PropTypes.string,
    inputLabelText: PropTypes.string,
    inputFieldChangeHandler: PropTypes.func,
    storesJSONData: PropTypes.array,
    searchInputRef: PropTypes.object
};

export default LocationSearchBar;
